import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import VendorDirectory from '../components/vendor/management/VendorDirectory';
import VendorProfile from '../components/vendor/management/VendorProfile';
import VendorAdminPageHeader from '../components/vendor/VendorAdminPageHeader';
import HubQuickSearch from '../components/search/HubQuickSearch';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchVendorsHub } from '../utils/hubSearch/searchSecondaryHubs';
import { HubSearchResult } from '../types/hubSearch';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import {
  buildVendorOrgDirectoryRows,
  filterVendorDirectoryRows,
  getInvitesForOrg,
  getLinkedProjectsForOrg,
  getLinkedPortalUsersForOrg,
  type VendorCapabilityFilter,
} from '../utils/vendorOrgSummary';
import '../components/pm/pm-shared.css';
import '../components/ops/OpsSidebar.css';
import './OperationsHub.css';
import './VendorManagementPage.css';

const VendorManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    supplierOrgs,
    supplierInvites,
    rfqProjects,
    timberProjects,
    crmUsers,
    createSupplierOrg,
    revokeSupplierInvite,
    deleteSupplierInvite,
    deleteSupplierOrg,
  } = useCRM();

  const [searchQuery, setSearchQuery] = useState('');
  const [capabilityFilter, setCapabilityFilter] = useState<VendorCapabilityFilter>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [deleteVendorBusy, setDeleteVendorBusy] = useState(false);
  const [showQuickSearch, setShowQuickSearch] = useState(false);

  const selectedOrgId = searchParams.get('org');

  const directoryRows = useMemo(
    () => buildVendorOrgDirectoryRows(supplierOrgs, supplierInvites, rfqProjects),
    [supplierOrgs, supplierInvites, rfqProjects],
  );

  const filteredRows = useMemo(
    () => filterVendorDirectoryRows(directoryRows, searchQuery, capabilityFilter),
    [directoryRows, searchQuery, capabilityFilter],
  );

  const selectedOrg = useMemo(
    () => (selectedOrgId ? supplierOrgs.find((o) => o.id === selectedOrgId) ?? null : null),
    [supplierOrgs, selectedOrgId],
  );

  const selectedInvites = useMemo(
    () => (selectedOrgId ? getInvitesForOrg(selectedOrgId, supplierInvites) : []),
    [selectedOrgId, supplierInvites],
  );

  const selectedLinkedProjects = useMemo(
    () => (selectedOrgId ? getLinkedProjectsForOrg(selectedOrgId, rfqProjects, timberProjects) : []),
    [selectedOrgId, rfqProjects, timberProjects],
  );

  const selectedLinkedPortalUsers = useMemo(
    () => (selectedOrgId ? getLinkedPortalUsersForOrg(selectedOrgId, crmUsers) : []),
    [selectedOrgId, crmUsers],
  );

  const timberCount = useMemo(
    () => directoryRows.filter((r) => r.capabilities.includes('timber')).length,
    [directoryRows],
  );

  const cncCount = useMemo(
    () => directoryRows.filter((r) => r.capabilities.includes('cnc')).length,
    [directoryRows],
  );

  const selectOrg = useCallback(
    (orgId: string) => {
      setSearchParams({ org: orgId });
    },
    [setSearchParams],
  );

  const clearSelectedOrg = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  useHubQuickSearchHotkey({
    onOpen: () => setShowQuickSearch(true),
    blocked: showQuickSearch || showCreateForm,
  });

  const getVendorQuickSearchResults = useCallback(
    (query: string) => searchVendorsHub(query, directoryRows),
    [directoryRows],
  );

  const handleVendorQuickSearchSelect = useCallback(
    (result: HubSearchResult) => {
      selectOrg(result.id);
    },
    [selectOrg],
  );

  const handleDeleteVendor = useCallback(async () => {
    if (!selectedOrg?.id) {
      throw new Error('No vendor selected.');
    }
    setDeleteVendorBusy(true);
    try {
      await deleteSupplierOrg(selectedOrg.id);
      clearSelectedOrg();
    } finally {
      setDeleteVendorBusy(false);
    }
  }, [selectedOrg, deleteSupplierOrg, clearSelectedOrg]);

  const handleCreateOrg = useCallback(async () => {
    if (!createName.trim() || !user?.id) return;
    setCreateBusy(true);
    try {
      const id = await createSupplierOrg({
        name: createName.trim(),
        capabilities: ['timber'],
        createdByUid: user.id,
        createdByName: user.name,
      });
      setCreateName('');
      setShowCreateForm(false);
      selectOrg(id);
    } finally {
      setCreateBusy(false);
    }
  }, [createName, user?.id, user?.name, createSupplierOrg, selectOrg]);

  const isFiltering = Boolean(searchQuery.trim()) || capabilityFilter !== 'all';

  return (
    <div className="ops-hub vm-page">
      <AppHeader />
      <div className="ops-hub-layout vm-hub-layout">
        <VendorDirectory
          rows={filteredRows}
          selectedOrgId={selectedOrgId}
          searchQuery={searchQuery}
          capabilityFilter={capabilityFilter}
          showCreateForm={showCreateForm}
          createName={createName}
          createBusy={createBusy}
          onSearchChange={setSearchQuery}
          onFilterChange={setCapabilityFilter}
          onSelectOrg={selectOrg}
          onToggleCreate={() => setShowCreateForm((v) => !v)}
          onCreateNameChange={setCreateName}
          onCreateSubmit={handleCreateOrg}
        />

        <div className="ops-hub-main">
          <div className="ops-hub-toolbar">
            <div className="ops-hub-toolbar-left">
              <h2 className="ops-hub-title">{selectedOrg?.name ?? 'Vendors'}</h2>
              <div className="ops-hub-stats">
                <span className="ops-stat">{directoryRows.length} total</span>
                {isFiltering && filteredRows.length !== directoryRows.length && (
                  <span className="ops-stat ops-stat-active">{filteredRows.length} shown</span>
                )}
                {timberCount > 0 && <span className="ops-stat">{timberCount} timber</span>}
                {cncCount > 0 && <span className="ops-stat">{cncCount} CNC</span>}
                {selectedOrg && selectedLinkedPortalUsers.length > 0 && (
                  <span className="ops-stat ops-stat-active">
                    {selectedLinkedPortalUsers.length} linked account
                    {selectedLinkedPortalUsers.length === 1 ? '' : 's'}
                  </span>
                )}
                {selectedOrg && selectedInvites.length > 0 && (
                  <span className="ops-stat ops-stat-done">
                    {selectedInvites.length} portal user{selectedInvites.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>
            <div className="ops-hub-toolbar-right">
              <VendorAdminPageHeader supplierOrgs={supplierOrgs} />
            </div>
          </div>

          <div className="ops-hub-content vm-hub-content pm-scroll">
            {!selectedOrg ? (
              <div className="ops-table-empty vm-empty-profile">
                <Building2 size={32} strokeWidth={1.25} aria-hidden />
                <p>Select a vendor</p>
                <span>Choose a company from the directory or add a new vendor to configure details and portal access.</span>
              </div>
            ) : (
              <VendorProfile
                org={selectedOrg}
                invites={selectedInvites}
                linkedProjects={selectedLinkedProjects}
                linkedPortalUsers={selectedLinkedPortalUsers}
                onRevokeInvite={(id) => void revokeSupplierInvite(id)}
                onDeleteInvite={(id) => void deleteSupplierInvite(id)}
                onDeleteVendor={handleDeleteVendor}
                deleteVendorBusy={deleteVendorBusy}
              />
            )}
          </div>
        </div>
      </div>

      <HubQuickSearch
        open={showQuickSearch}
        onClose={() => setShowQuickSearch(false)}
        getResults={getVendorQuickSearchResults}
        onSelect={handleVendorQuickSearchSelect}
        placeholder="Search vendor organizations…"
        ariaLabel="Vendor quick search"
        renderIcon={() => <Building2 size={16} />}
      />
    </div>
  );
};

export default VendorManagementPage;
